import { FC, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Collapse, Stack } from '@mui/material';
import {
  Panel,
  Footer,
  Button,
  DataLossPrompt,
  SwitchInput,
  RadioInput,
  TextInput,
} from 'melp-design/components';
import { useMe } from 'state/Administrators';
import { Controller, SubmitHandler, useForm } from 'react-hook-form-latest';
import {
  ClaimBenefitCombination,
  claimBenefitCombinations,
  useUpdateParentCompanySettings,
} from 'store/companies';
import { usePredefinedToasts } from 'utils/Toast';

interface Values {
  expenseCompensationAskIban: boolean;
  companyExpenseEnabled: boolean;
  companyExpenseEmail: string;
  expenseCompensationBenefitCombination: ClaimBenefitCombination;
}

const INITIAL_VALUES: Values = {
  expenseCompensationAskIban: false,
  companyExpenseEnabled: false,
  companyExpenseEmail: '',
  expenseCompensationBenefitCombination: 'any',
};

export const ClaimsSettings: FC = () => {
  const { t } = useTranslation();
  const { me, hasEditPermission } = useMe();
  const canEdit = hasEditPermission(['expenseCompensations']);
  const predefinedToasts = usePredefinedToasts();

  const { mutate: updateCompanySettings, isLoading: isUpdating } =
    useUpdateParentCompanySettings();

  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { isDirty },
  } = useForm<Values>({
    defaultValues: INITIAL_VALUES,
  });

  useEffect(() => {
    if (me?.parentCompany) {
      reset({
        expenseCompensationAskIban:
          me.parentCompany.settings.expenseCompensationAskIban,
        companyExpenseEnabled: me.parentCompany.settings.companyExpenseEnabled,
        companyExpenseEmail: me.parentCompany.settings.companyExpenseEmail,
        expenseCompensationBenefitCombination:
          me.parentCompany.settings.expenseCompensationBenefitCombination,
      });
    }
  }, [me, reset]);

  const onSubmit: SubmitHandler<Values> = (values) => {
    updateCompanySettings(values, {
      onSuccess: () => {
        predefinedToasts.success.updated();
      },
    });
  };

  const [companyExpenseEnabled] = watch(['companyExpenseEnabled']);

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Stack gap={2}>
          <Panel title={t('sidebar.companySettingsGeneralSettings')}>
            <Stack spacing={2}>
              <Controller
                name="expenseCompensationAskIban"
                control={control}
                render={({ field: { name, value, onChange, ref } }) => {
                  return (
                    <SwitchInput
                      label={t('claims.expenseCompensationAskIban.label')}
                      name={name}
                      value={value}
                      onChange={onChange}
                      disabled={!canEdit}
                      ref={ref}
                    />
                  );
                }}
              />

              <Controller
                name="companyExpenseEnabled"
                control={control}
                render={({ field: { name, value, onChange, ref } }) => {
                  return (
                    <SwitchInput
                      label={t('claims.companyExpenseEnabled.label')}
                      name={name}
                      value={value}
                      onChange={onChange}
                      disabled={!canEdit}
                      ref={ref}
                    />
                  );
                }}
              />

              <Collapse in={companyExpenseEnabled}>
                <Stack>
                  <Controller
                    name="companyExpenseEmail"
                    control={control}
                    render={({ field: { name, value, onChange, ref } }) => {
                      return (
                        <TextInput
                          label={t('claims.companyExpenseEmail.label')}
                          name={name}
                          value={value}
                          onChange={onChange}
                          type="email"
                          required={companyExpenseEnabled}
                          disabled={!canEdit}
                          ref={ref}
                        />
                      );
                    }}
                  />
                </Stack>
              </Collapse>

              <Controller
                name="expenseCompensationBenefitCombination"
                control={control}
                render={({ field: { name, value, onChange, ref } }) => {
                  return (
                    <RadioInput
                      label={t('claims.settings.claimBenefitCombination.label')}
                      name={name}
                      value={value}
                      onChange={onChange}
                      items={claimBenefitCombinations.map((method) => ({
                        label: t(`claims.benefit_combination.${method}`),
                        value: method,
                      }))}
                      disabled={!canEdit}
                      ref={ref}
                    />
                  );
                }}
              />
            </Stack>
          </Panel>
        </Stack>

        <Footer visible={isDirty}>
          <Stack direction="row" justifyContent="end" gap={2}>
            <Button
              label={t('common.cancel')}
              variant="neutral-outline"
              onClick={() => {
                reset(undefined, { keepDirty: false });
              }}
            />
            <Button
              label={t('common.save')}
              type="submit"
              disabled={isUpdating || !canEdit}
            />
          </Stack>
        </Footer>
      </form>

      <DataLossPrompt when={canEdit && isDirty} />
    </>
  );
};
